/**
 * feat-302 — what the audience chips need from the page, in one context.
 *
 * `null` outside `/quiz-review` (and in the card-level tests), which renders no
 * chip at all — the banners then look exactly as feat-298/299 shipped them.
 */
import React, { createContext, useContext } from 'react';
import { Audience, AudienceOptions } from '../../../../services/quizContentAdminService';
import { AudienceLevel } from './audienceCopy';

/** A Track a Lesson belongs to, for the Lesson editor's Track note. */
export interface TrackRef {
  id: string;
  title: string;
  audience: Audience;
}

/** Loading state of the tenant's location/role lists. `failed` is NEVER read
 *  as an empty list (feat-301 rule): the dimension is locked as it is. */
export type OptionsState =
  | { status: 'loading' }
  | { status: 'ready'; options: AudienceOptions }
  | { status: 'failed' };

export interface AudienceApi {
  options: OptionsState;
  /** Re-read the option lists (the editor's "Try again"). */
  retryOptions: () => void;
  /** The Tracks that list this Lesson, in Track order. Empty for an orphan. */
  tracksForLesson: (lessonId: string) => TrackRef[];
  /**
   * Write the WHOLE audience (ADR §10.3). Resolves once the in-memory tree is
   * patched with the server's canonical object; rejects on failure (the editor
   * keeps the draft and shows the error).
   */
  save: (level: AudienceLevel, id: string, audience: Audience) => Promise<void>;
  /**
   * View as (NOTES §6.7): non-null when this row was just edited under View as
   * and the view no longer includes it — the "Saved. … no longer sees this" line.
   */
  editedOutLine: (level: AudienceLevel, id: string) => string | null;
  /** The active View as name ("Alameda · any role"), or null. */
  viewName: string | null;
}

export const AudienceContext = createContext<AudienceApi | null>(null);

export function useAudience(): AudienceApi | null {
  return useContext(AudienceContext);
}

export function readyOptions(state: OptionsState): AudienceOptions | null {
  return state.status === 'ready' ? state.options : null;
}

export const AudienceProvider: React.FC<{ value: AudienceApi | null; children: React.ReactNode }> = ({
  value,
  children,
}) => <AudienceContext.Provider value={value}>{children}</AudienceContext.Provider>;
